import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { codeToSlug } from "../../data/country-slugs";
import { CountryFlag } from "../ui/CountryFlag";
import { EASE_EXPO, KineticHeading, Reveal } from "../motion/primitives";

const INK = "#0A0F0E";
const BRAND_DARK = "#00332F";
const TEAL_DEEP = "#00786F";
const SECONDARY = "#46504D";

const TOP_COUNTRIES = [
  { code: "IN", name: "India" },
  { code: "TR", name: "Turkey" },
  { code: "US", name: "United States" },
  { code: "PK", name: "Pakistan" },
  { code: "NG", name: "Nigeria" },
  { code: "CN", name: "China" },
  { code: "RU", name: "Russia" },
  { code: "UA", name: "Ukraine" },
  { code: "KZ", name: "Kazakhstan" },
  { code: "BD", name: "Bangladesh" },
  { code: "EG", name: "Egypt" },
  { code: "AZ", name: "Azerbaijan" },
  { code: "UZ", name: "Uzbekistan" },
  { code: "JP", name: "Japan" },
  { code: "BR", name: "Brazil" },
];

/**
 * Guides by nationality: one big tile per country, each a link to that
 * country's guide. The flag tips and the arrow lifts on hover.
 */
export function NationalityGuides() {
  return (
    <section className="py-20 md:py-24" style={{ backgroundColor: "#FFFFFF" }}>
      <div className="mx-auto max-w-[1120px] px-5 sm:px-8">
        <div className="flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-2xl">
            <KineticHeading
              text="A guide for your country"
              className="font-black leading-[0.95] tracking-[-0.04em] [text-wrap:balance]"
              style={{ fontSize: "var(--text-h2)", color: INK }}
            />
            <Reveal delay={0.2}>
              <p className="mt-5 text-[18px] leading-relaxed" style={{ color: SECONDARY }}>
                What your documents need, where you apply and the money to show, in your currency.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.25}>
            <Link to="/guides" className="group inline-flex shrink-0 items-center gap-1.5 text-[17px] font-bold" style={{ color: BRAND_DARK }}>
              <span className="underline decoration-2 underline-offset-[6px] group-hover:decoration-[#2EE6D6]">All guides</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
            </Link>
          </Reveal>
        </div>

        <ul className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 md:mt-12 lg:grid-cols-5">
          {TOP_COUNTRIES.map((c, i) => (
            <motion.li
              key={c.code}
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.6, delay: (i % 5) * 0.05 + Math.floor(i / 5) * 0.08, ease: EASE_EXPO }}
            >
              <Link
                to={`/${codeToSlug[c.code]}`}
                className="group flex h-full items-center gap-3 border border-[#E0E3DE] bg-white px-4 py-4 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-[#00786F] hover:shadow-[0_16px_34px_rgba(10,15,14,0.08)]"
                style={{ borderRadius: 20 }}
              >
                <span className="transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-110">
                  <CountryFlag code={c.code} size="md" circle animate={false} />
                </span>
                <span className="min-w-0 flex-1 truncate text-[15.5px] font-extrabold tracking-[-0.01em]" style={{ color: INK }}>
                  {c.name}
                </span>
                <ArrowUpRight
                  className="h-4 w-4 shrink-0 opacity-0 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:opacity-100"
                  style={{ color: TEAL_DEEP }}
                  aria-hidden="true"
                />
              </Link>
            </motion.li>
          ))}
        </ul>
      </div>
    </section>
  );
}
